import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PhotoGallery from "../components/PhotoGallery";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, FilePdf, FileXls, Sparkle, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  { key: "tyre_register", label: "Complete Tyre Register" },
  { key: "fitment", label: "Tyre Fitment Report" },
  { key: "removal", label: "Tyre Removal Report" },
  { key: "puncture", label: "Puncture / Damage Report" },
  { key: "inspection", label: "Tyre Inspection Report" },
  { key: "rotation", label: "Tyre Rotation Report" },
  { key: "pressure", label: "Tyre Pressure Report" },
];

const DAILY_SECTIONS = [
  { key: "fitments", label: "Fitments", cols: [["Serial", "serial_number"], ["Dumper", "dumper_number"], ["Position", "position"], ["Condition", "condition"], ["Engine Hrs", "engine_hours"], ["By", "technician"]], photoKeys: ["photos"] },
  { key: "removals", label: "Removals", cols: [["Serial", "serial_number"], ["Dumper", "dumper_number"], ["Position", "position"], ["Reason", "reason"], ["Tread", "tread_depth"], ["By", "technician"]], photoKeys: ["photos"] },
  { key: "rotations", label: "Rotations", cols: [["Serial", "serial_number"], ["To Dumper", "new_dumper_number"], ["Position", "new_position"], ["Engine Hrs", "engine_hours"], ["By", "technician"]] },
  { key: "inspections", label: "Inspections", cols: [["Serial", "serial_number"], ["Dumper", "dumper_number"], ["Tread", "tread_depth"], ["Pressure", "pressure"], ["Action", "recommended_action"], ["By", "technician"]], photoKeys: ["photos"] },
  { key: "punctures", label: "Punctures / Damage", cols: [["Report", "report_number"], ["Serial", "serial_number"], ["Type", "puncture_type"], ["Cost", "repair_cost"], ["By", "technician"]], photoKeys: ["photos_before", "photos_after"] },
  { key: "pressure_checks", label: "Pressure Checks", cols: [["Serial", "serial_number"], ["Actual", "actual_pressure"], ["Recommended", "recommended_pressure"], ["Diff", "difference"], ["By", "technician"]] },
];

const cell = (v) => (v === true ? "Yes" : v === false ? "No" : v == null || v === "" ? "—" : typeof v === "number" ? v.toLocaleString() : String(v));
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("all");
  const [vehClass, setVehClass] = useState("all");
  const [summary, setSummary] = useState(null);
  const [avgLife, setAvgLife] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [dailyDate, setDailyDate] = useState(todayStr());
  const [daily, setDaily] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(false);

  useEffect(() => { api.get("/sites").then(({ data }) => setSites(data)); }, []);
  useEffect(() => {
    const qs = siteId !== "all" ? `?mine_site_id=${siteId}` : "";
    api.get(`/dashboard/stats${qs}`).then(({ data }) => setAvgLife(data.avg_tyre_life_hours));
  }, [siteId]);

  const download = async (key, fmt) => {
    try {
      const p = new URLSearchParams();
      if (siteId !== "all") p.set("mine_site_id", siteId);
      if (vehClass !== "all") p.set("vehicle_class", vehClass);
      const { data } = await api.get(`/reports/${key}/${fmt}?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const genSummary = async () => {
    setLoadingSummary(true);
    try {
      const qp = new URLSearchParams();
      if (siteId !== "all") qp.set("mine_site_id", siteId);
      if (vehClass !== "all") qp.set("vehicle_class", vehClass);
      const qs = qp.toString();
      const { data } = await api.get(`/reports/monthly-summary${qs ? `?${qs}` : ""}`);
      setSummary(data);
    } finally { setLoadingSummary(false); }
  };

  const loadDaily = async () => {
    setLoadingDaily(true);
    try {
      const p = new URLSearchParams({ date: dailyDate });
      if (siteId !== "all") p.set("mine_site_id", siteId);
      if (vehClass !== "all") p.set("vehicle_class", vehClass);
      const { data } = await api.get(`/reports/daily?${p.toString()}`);
      setDaily(data);
    } catch (e) { toast.error("Could not load daily report"); }
    finally { setLoadingDaily(false); }
  };

  const downloadDaily = async (fmt) => {
    try {
      const p = new URLSearchParams({ date: dailyDate });
      if (siteId !== "all") p.set("mine_site_id", siteId);
      if (vehClass !== "all") p.set("vehicle_class", vehClass);
      const { data } = await api.get(`/reports/daily/${fmt}?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `daily_report_${dailyDate}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline text-amber-500 flex items-center gap-2"><FileText size={14} /> Exports</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Reports</h1>
        </div>
        <div className="flex gap-2">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger data-testid="report-site-filter" className="w-52 bg-zinc-900"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Mine Sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vehClass} onValueChange={setVehClass}>
            <SelectTrigger data-testid="report-class-filter" className="w-40 bg-zinc-900"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vehicles</SelectItem>
              <SelectItem value="Dumper">Dumpers</SelectItem>
              <SelectItem value="Truck">Trucks</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div data-testid="avg-life-card" className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="overline text-zinc-400">Average Tyre Life</div>
          <div className="mt-2 font-display text-3xl font-extrabold tabular-nums text-amber-500">
            {avgLife != null ? avgLife.toLocaleString() : "—"} <span className="text-sm text-zinc-400">hrs</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {REPORTS.map((r) => (
          <div key={r.key} data-testid={`report-${r.key}`} className="flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-900 p-4">
            <span className="text-sm font-semibold">{r.label}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => download(r.key, "excel")} data-testid={`excel-${r.key}`}><FileXls size={15} className="mr-1 text-emerald-400" /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => download(r.key, "pdf")} data-testid={`pdf-${r.key}`}><FilePdf size={15} className="mr-1 text-rose-400" /> PDF</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5" data-testid="daily-report">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2"><CalendarBlank size={18} className="text-amber-500" /> Daily Activity Report</h2>
            <p className="text-xs text-zinc-500">Everything that happened on a single day — to share with the authorities.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" data-testid="daily-date" value={dailyDate} max={todayStr()} onChange={(e) => setDailyDate(e.target.value)} className="w-40 bg-zinc-950" />
            <Button size="sm" onClick={loadDaily} disabled={loadingDaily} data-testid="daily-view-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">{loadingDaily ? "Loading…" : "View"}</Button>
            <Button size="sm" variant="outline" onClick={() => downloadDaily("excel")} data-testid="daily-excel-btn"><FileXls size={15} className="mr-1 text-emerald-400" /> Excel</Button>
            <Button size="sm" variant="outline" onClick={() => downloadDaily("pdf")} data-testid="daily-pdf-btn"><FilePdf size={15} className="mr-1 text-rose-400" /> PDF</Button>
          </div>
        </div>

        {daily && (
          <div className="mt-5 space-y-5" data-testid="daily-result">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-sm bg-zinc-950 px-2 py-1 text-zinc-400">{daily.site}</span>
              <span className="rounded-sm bg-amber-500/10 px-2 py-1 font-semibold text-amber-500">{daily.counts.total} total {daily.counts.total === 1 ? "activity" : "activities"}</span>
              {DAILY_SECTIONS.filter((s) => daily.counts[s.key] > 0).map((s) => (
                <span key={s.key} className="rounded-sm bg-zinc-950 px-2 py-1 text-zinc-400">{s.label}: <span className="font-mono text-zinc-200">{daily.counts[s.key]}</span></span>
              ))}
            </div>

            {daily.counts.total === 0 ? (
              <p className="rounded-sm border border-dashed border-zinc-800 bg-zinc-950 px-4 py-8 text-center text-sm text-zinc-500">No tyre activity was recorded on {new Date(daily.date).toLocaleDateString()}.</p>
            ) : (
              DAILY_SECTIONS.filter((s) => (daily[s.key] || []).length > 0).map((s) => {
                const rows = daily[s.key];
                const hasPhotos = !!s.photoKeys;
                return (
                  <div key={s.key} data-testid={`daily-section-${s.key}`}>
                    <div className="overline mb-2 text-zinc-500">{s.label} ({rows.length})</div>
                    <div className="overflow-x-auto rounded-sm border border-zinc-800">
                      <table className="w-full min-w-[560px] text-xs">
                        <thead className="bg-zinc-950 text-left text-zinc-500"><tr>
                          {s.cols.map((c) => <th key={c[1]} className="px-2 py-1.5">{c[0]}</th>)}
                          {hasPhotos && <th className="px-2 py-1.5">Photos</th>}
                        </tr></thead>
                        <tbody className="divide-y divide-zinc-800">
                          {rows.map((r) => {
                            const rowPhotos = hasPhotos ? s.photoKeys.flatMap((pk) => r[pk] || []).filter((p) => p && p.id) : [];
                            return (
                              <tr key={r.id} className="bg-zinc-900">
                                {s.cols.map((c) => <td key={c[1]} className="px-2 py-1.5 tabular-nums">{cell(r[c[1]])}</td>)}
                                {hasPhotos && (
                                  <td className="px-2 py-1.5">
                                    {rowPhotos.length > 0 ? <PhotoGallery photos={rowPhotos} thumb="h-10 w-10" testid={`daily-photo-${s.key}-${r.id}`} /> : <span className="text-zinc-600">—</span>}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">Monthly Customer Report</h2>
          <Button size="sm" onClick={genSummary} disabled={loadingSummary} className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            <Sparkle size={15} weight="bold" className="mr-1" /> {loadingSummary ? "Generating…" : "Generate"}
          </Button>
        </div>

        {summary && (
          <div className="mt-5 space-y-5" data-testid="monthly-summary">
            <div>
              <div className="overline mb-2 text-zinc-500">Executive Summary</div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {Object.entries(summary.executive_summary).map(([k, v]) => (
                  <div key={k} className="rounded-sm border border-zinc-800 bg-zinc-950 p-3">
                    <div className="overline text-zinc-500">{k.replace(/_/g, " ")}</div>
                    <div className="mt-1 font-display text-xl font-extrabold tabular-nums text-amber-500">{typeof v === "number" ? v.toLocaleString() : v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-4">
                <div className="overline mb-2 text-zinc-500">Performance</div>
                <div className="text-sm text-zinc-300">Avg life by type:</div>
                <ul className="mt-1 text-xs text-zinc-400">
                  {summary.performance.avg_life_by_type.map((x) => <li key={x.name}>{x.name}: <span className="font-mono text-amber-400">{x.value.toLocaleString()} h</span></li>)}
                </ul>
                <div className="mt-2 text-xs text-zinc-400">High-puncture dumpers: {summary.performance.high_puncture_vehicles.map((h) => `${h.fleet} (${h.count})`).join(", ") || "none"}</div>
              </div>
              <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-4">
                <div className="overline mb-2 text-zinc-500">Recommendations</div>
                <ul className="list-inside list-disc space-y-1 text-sm text-zinc-300">
                  {summary.recommendations.map((r, i) => <li key={`rec-${i}-${r.slice(0, 12)}`}>{r}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
